import { CommonModule } from '@angular/common';
import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Payloads } from '../../../../model/payloads.model';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from '../../../../common/common';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import {BehaviorSubject, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: "app-payload",
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: "./payload-index.component.html",
  styleUrl: "./payload-index.component.scss",
})
export class PayloadIndexComponent implements OnInit, OnDestroy {
  // Search fields
  public idSearch: string = '';
  public venIdSearch: string = '';
  public requestIdSearch: string = '';
  public startTimeSearch: string = '';
  public endTimeSearch: string = '';
  public sentTypeSearch: string = '';
  public receivedTypeSearch: string = '';

  // Debounce search
  private searchParams$ = new BehaviorSubject<any>({
    id: '',
    venId: '',
    requestId: '',
    startTime: '',
    endTime: '',
    sentType: '',
    receivedType: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'payloadsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public displayedPayloads: Payloads[] = [];
  public isLoading: boolean = false;
  public error: string | null = null;

  public router: Router;

  constructor(
    private injector: Injector,
    private payloadsService: ApiService
  ) {
    this.router = injector.get(Router);

    // Setup debounced search
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.fetchPayloads();
    });
  }

  ngOnInit(): void {
    this.paginationModel = this.pagination.resetPagination();
    this.fetchPayloads();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  fetchPayloads() {
    this.isLoading = true;

    // Build parameters
    const queryParams = this.buildSearchParams();
    this.payloadsService.getPayloads(queryParams).subscribe({
      next: (response: any) => {
        this.displayedPayloads = response.map((payload: Payloads) => payload);
        this.paginationModel = this.pagination.updateTotalItems(response.length);
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = extractErrorMessage(err, 'Failed to load payload');
        this.isLoading = false;
      }
    });
  }

  buildSearchParams(): CustomHttpParams {
    let params: CustomHttpParams = {
      'depth': 1,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.idSearch) {
      params['id'] = this.idSearch;
    }

    if (this.venIdSearch) {
      params['ven_id'] = this.venIdSearch;
    }

    if (this.requestIdSearch) {
      params['request_id'] = encodeURIComponent(this.requestIdSearch.trim());
    }
    if (this.startTimeSearch && moment(this.startTimeSearch.trim(), moment.ISO_8601).isValid()) {
      params['start_dttm'] = this.startTimeSearch.trim();
      params['time_filter'] = 'sent_dttm';
    } else if (this.endTimeSearch && moment(this.endTimeSearch.trim(), moment.ISO_8601).isValid()) {
      params['end_dttm'] = this.endTimeSearch.trim();
      params['time_filter'] = 'sent_dttm';
    }
    if (this.sentTypeSearch) {
      params['sent_type'] = this.sentTypeSearch.trim();
    }

    if (this.receivedTypeSearch) {
      params['received_type'] = this.receivedTypeSearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();
    this.searchParams$.next({
      id: this.idSearch,
      venId: this.venIdSearch,
      requestId: this.requestIdSearch,
      startTime: this.startTimeSearch,
      endTime: this.endTimeSearch,
      sentType: this.sentTypeSearch,
      receivedType: this.receivedTypeSearch
    });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.fetchPayloads();
  }

  clickDetailPayloadPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }
}
